import clsx from "clsx";
import type { AufmassField, AufmassSection, AufmassValue } from "@/lib/aufmass";

function isSelected(value: AufmassValue | undefined, token: string): boolean {
  if (Array.isArray(value)) return value.includes(token);
  return value === token;
}

export function BrandschutzBlattForm({
  schema,
  felder,
  auftraggeber,
  auftragsnummer,
  disabled,
  onChange,
}: {
  schema: AufmassSection[];
  felder: Record<string, AufmassValue>;
  auftraggeber?: string | null;
  auftragsnummer?: string | null;
  disabled: boolean;
  onChange: (fieldId: string, value: AufmassValue) => void;
}) {
  return (
    <div className="space-y-5">
      <div className="grid gap-2 rounded-xl border border-separator bg-fill-quaternary px-3 py-2 text-sm sm:grid-cols-2">
        <div>
          <div className="text-xs text-label-secondary">Auftraggeber</div>
          <div>{auftraggeber || "–"}</div>
        </div>
        <div>
          <div className="text-xs text-label-secondary">Auftrag</div>
          <div>{auftragsnummer || "–"}</div>
        </div>
      </div>
      {schema.map((section) => (
        <section key={section.id} className="space-y-2">
          <h3 className="text-sm font-semibold text-label">{section.title}</h3>
          <div className="divide-y divide-separator rounded-xl border border-separator">
            {section.fields.map((field) => (
              <FieldRow
                key={field.id}
                field={field}
                value={felder[field.id]}
                disabled={disabled}
                onChange={onChange}
              />
            ))}
          </div>
        </section>
      ))}
    </div>
  );
}

function FieldRow({
  field,
  value,
  disabled,
  onChange,
}: {
  field: AufmassField;
  value: AufmassValue | undefined;
  disabled: boolean;
  onChange: (fieldId: string, value: AufmassValue) => void;
}) {
  return (
    <div className="grid gap-2 px-3 py-2 sm:grid-cols-[minmax(12rem,1fr)_minmax(14rem,1.1fr)] sm:items-start">
      <div>
        <div className="text-sm text-label">{field.label}</div>
        {field.hint ? <div className="text-xs text-label-secondary">{field.hint}</div> : null}
      </div>
      <FieldControl field={field} value={value} disabled={disabled} onChange={onChange} />
    </div>
  );
}

function FieldControl({
  field,
  value,
  disabled,
  onChange,
}: {
  field: AufmassField;
  value: AufmassValue | undefined;
  disabled: boolean;
  onChange: (fieldId: string, value: AufmassValue) => void;
}) {
  if (field.options.length > 0) {
    return (
      <div className="flex flex-wrap gap-1">
        {field.options.map((option) => {
          const active = isSelected(value, option.value);
          return (
            <button
              key={option.value}
              type="button"
              disabled={disabled}
              className={clsx(
                "rounded-md border px-2 py-1 text-xs",
                active
                  ? "border-accent bg-accent text-label-on-accent"
                  : "border-separator bg-card text-label",
              )}
              onClick={() => {
                if (field.kind === "multi") {
                  const current = Array.isArray(value) ? value : [];
                  const next = active
                    ? current.filter((item) => item !== option.value)
                    : [...current, option.value];
                  onChange(field.id, next);
                  return;
                }
                onChange(field.id, active ? "" : option.value);
              }}
            >
              {option.label}
            </button>
          );
        })}
      </div>
    );
  }

  if (field.kind === "long") {
    return (
      <textarea
        className="eh-input min-h-[4rem] w-full px-2 py-1 text-sm"
        value={typeof value === "string" ? value : ""}
        disabled={disabled}
        aria-label={field.label}
        onChange={(event) => onChange(field.id, event.target.value)}
      />
    );
  }

  return (
    <div className="flex items-center gap-2">
      <input
        className="eh-input w-full px-2 py-1 text-sm"
        value={typeof value === "string" ? value : ""}
        disabled={disabled}
        inputMode={field.kind === "mm" ? "decimal" : "text"}
        aria-label={field.label}
        onChange={(event) => onChange(field.id, event.target.value)}
      />
      {field.kind === "mm" ? <span className="text-xs text-label-secondary">mm</span> : null}
    </div>
  );
}
